import type {CancelablePromise} from '../core/CancelablePromise';
import {OpenAPI} from '../core/OpenAPI';
import {request as __request} from '../core/request';
import {ApiError} from '../core/ApiError';

export type DocumentInfo = {
    id: string;
    title: string;
    filename: string;
    modified_at: string | null;
};

export type DocumentList = {
    documents: DocumentInfo[];
};

export type DocumentSign = {
    id: number;
    title: string;
    icon: string;
    subtitle: string;
    sort_order: number;
};

export type DocumentSignList = {
    signs: DocumentSign[];
};

export type DocumentSignCreate = {
    title?: string;
    icon?: string;
    subtitle?: string;
};

export type DocumentSignUpdate = {
    title?: string;
    icon?: string;
    subtitle?: string;
};

function authHeaders(): Record<string, string> {
    const headers = OpenAPI.HEADERS;
    if (!headers || typeof headers === 'function') {
        return {};
    }
    return {...headers};
}

export class DocumentsService {
    public static getDocumentsApiV1DocumentsGet(): CancelablePromise<DocumentList> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/documents',
        });
    }

    public static generateDocumentsApiV1DocumentsGeneratePost(): CancelablePromise<DocumentList> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/v1/documents/generate',
        });
    }

    public static getDocumentSignsApiV1DocumentsSignsGet(): CancelablePromise<DocumentSignList> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/documents/signs',
        });
    }

    public static createDocumentSignApiV1DocumentsSignsPost({
        requestBody,
    }: {
        requestBody: DocumentSignCreate;
    }): CancelablePromise<DocumentSign> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/v1/documents/signs',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    public static updateDocumentSignApiV1DocumentsSignsSignIdPut({
        signId,
        requestBody,
    }: {
        signId: number;
        requestBody: DocumentSignUpdate;
    }): CancelablePromise<DocumentSign> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: `/api/v1/documents/signs/${signId}`,
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    public static deleteDocumentSignApiV1DocumentsSignsSignIdDelete({
        signId,
    }: {
        signId: number;
    }): CancelablePromise<DocumentSign> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: `/api/v1/documents/signs/${signId}`,
        });
    }

    public static async getDocumentFileBlob(documentId: string): Promise<Blob> {
        const url = `${OpenAPI.BASE}/api/v1/documents/${encodeURIComponent(documentId)}/file`;
        const response = await fetch(url, {
            method: 'GET',
            headers: authHeaders(),
            credentials: OpenAPI.CREDENTIALS,
        });
        if (!response.ok) {
            let body: unknown;
            try {
                body = await response.json();
            } catch {
                body = await response.text();
            }
            throw new ApiError(
                {method: 'GET', url},
                {
                    url,
                    ok: false,
                    status: response.status,
                    statusText: response.statusText,
                    body,
                },
                'Request failed',
            );
        }
        return response.blob();
    }
}
